import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Edit, Delete, Close } from "@mui/icons-material";
import {
  Typography,
  Collapse,
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { isNil, pick } from "lodash";
import { DateTime } from "luxon";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import { enqueueSnackbar } from "notistack";
import { ReactElement, useEffect, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useQueryParam, withDefault, NumberParam } from "use-query-params";
import z from "zod";

import { TargetingForm, TargetingFormSchema } from "@stw/yup-validators";

import {
  ListAudienceSearchFiltersQuery,
  ListAudienceSearchFiltersQueryVariables,
  DeleteAudienceSearchFilterMutation,
  DeleteAudienceSearchFilterMutationVariables,
  EnqueueAudienceContactSearchMutation,
  EnqueueAudienceContactSearchMutationVariables,
  AudienceSearchFilterType,
  CreateAudienceSearchFilterMutation,
  CreateAudienceSearchFilterMutationVariables,
  GetAudienceSearchFilterQueryVariables,
  GetAudienceSearchFilterQuery,
  CampaignStatus,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import ExpandButton from "~frontend/components/ExpandButton";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import Table from "~frontend/components/Table";
import { convertTargetingFormToGraphQL } from "~frontend/components/audienceFilteringForm";
import { RhfTextField } from "~frontend/components/forms/react-hook-form-components";
import {
  LIST_AUDIENCE_SEARCH_FILTERS,
  DELETE_AUDIENCE_SEARCH_FILTER,
  ENQUEUE_AUDIENCE_CONTACT_SEARCH,
  CREATE_AUDIENCE_SEARCH_FILTER,
  GET_AUDIENCE_SEARCH_FILTER,
} from "~frontend/graphql/audience";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import { generateTargetGroupSentence } from "~frontend/util/generateTargetGroupSentence";
import { jobInProgress } from "~frontend/util/jobs";
import { plural } from "~frontend/util/plural";
import useMutation from "~frontend/util/useMutation";

import { SMALL_BUTTON_STYLE } from "./constants";
import { AudienceSearchFilterTargetingForm, useAudience } from "./store";
import { useCampaignNamesById } from "./useCampaignNamesById";

export function ReadableTargetGroupSummary({
  targetGroups,
}: {
  targetGroups: TargetingForm["targetGroups"];
}): ReactElement {
  const { donationHistoryPageEnabled } = useSubaccount()!;
  const includeDonationHistory = donationHistoryPageEnabled || false;

  const formContext = useFormContext<TargetingForm>();
  const values = formContext?.watch("targetGroups") ?? targetGroups;

  const { campaignNamesById } = useCampaignNamesById({
    skip: !includeDonationHistory,
    filter: {
      statusIn: [CampaignStatus.InitialSendComplete],
    },
  });

  return (
    <>
      {values.map((group, i) => {
        return (
          <div key={`target-summary-${i}`}>
            <Typography variant="body3" component="div">
              {generateTargetGroupSentence(group.clauses, campaignNamesById)}
            </Typography>
            {i < targetGroups.length - 1 && (
              <Typography variant="body3" component="div">
                OR
              </Typography>
            )}
          </div>
        );
      })}
    </>
  );
}

export function SearchResultHeading({
  filter,
}: {
  filter: AudienceSearchFilterTargetingForm;
}): ReactElement {
  const prefix =
    filter.type === AudienceSearchFilterType.SavedSearch
      ? filter.name
      : "Search Results";

  const suffix = isNil(filter.contactCount) ? (
    "Calculating..."
  ) : (
    <>
      {plural(filter.contactCount || 0, "contact", "contacts")} and{" "}
      {plural(
        filter.optedOutContactCount || 0,
        "opted out contact",
        "opted out contacts"
      )}
    </>
  );

  return (
    <Typography variant="body2Bold" component="div" sx={{ mb: 0.5 }}>
      {prefix}: {suffix}
    </Typography>
  );
}

const deleteFilterConfirmProps = (filterName: string): ConfirmOptions => ({
  title: (
    <>
      Delete <strong>{filterName}</strong>?
    </>
  ),
  buttonOrder: ["confirm", "cancel"],
  confirmationText: "Delete Saved Search",
  confirmationButtonProps: { variant: "outlined", color: "error" },
  cancellationText: "Close",
  cancellationButtonProps: { variant: "contained" },
  titleProps: { sx: { px: 5, pt: 3 } },
  dialogActionsProps: {
    sx: { justifyContent: "space-between", px: 5, pb: 5 },
  },
});

export function SavedSearchResultSummary({
  audienceId,
}: {
  audienceId: string;
}): ReactElement {
  const confirm = useConfirm();
  const { subaccount } = useSubaccount()!;

  const currentFilter = useAudience(store => store.currentFilter);
  const setFilter = useAudience(store => store.setFilter);
  const toggleIsEditingExistingFilter = useAudience(
    store => store.toggleIsEditingExistingFilter
  );
  const clearFilters = useAudience(store => store.clearFilters);
  const searchJobIsRunning = useAudience(store => store.searchJobIsRunning);
  const setSearchJobIsRunning = useAudience(
    store => store.setSearchJobIsRunning
  );

  const [nameModalOpen, setNameModalOpen] = useState<boolean>(false);
  const formMethods = useForm<{ name: string }>({
    resolver: zodResolver(z.object({ name: z.string().min(1) })),
    mode: "all",
    reValidateMode: "onBlur",
    defaultValues: { name: "" },
  });

  const { data, startPolling, stopPolling } = useQuery<
    GetAudienceSearchFilterQuery,
    GetAudienceSearchFilterQueryVariables
  >(GET_AUDIENCE_SEARCH_FILTER, {
    variables: {
      input: {
        audienceSearchFilterId: currentFilter.id!,
      },
    },
    skip: !currentFilter.id,
  });

  const [deleteFilter] = useMutation<
    DeleteAudienceSearchFilterMutation,
    DeleteAudienceSearchFilterMutationVariables
  >(DELETE_AUDIENCE_SEARCH_FILTER, {
    refetchQueries: [LIST_AUDIENCE_SEARCH_FILTERS],
  });

  const [createFilter] = useMutation<
    CreateAudienceSearchFilterMutation,
    CreateAudienceSearchFilterMutationVariables
  >(CREATE_AUDIENCE_SEARCH_FILTER, {
    refetchQueries: [LIST_AUDIENCE_SEARCH_FILTERS],
  });

  const [enqueueAudienceSearchJob] = useMutation<
    EnqueueAudienceContactSearchMutation,
    EnqueueAudienceContactSearchMutationVariables
  >(ENQUEUE_AUDIENCE_CONTACT_SEARCH, {
    onErrorSnackbarMessage: "Failed to apply the filters",
    awaitRefetchQueries: true,
    refetchQueries: [LIST_AUDIENCE_SEARCH_FILTERS],
  });

  async function handleDeleteFilter(filterId: string, filterName: string) {
    const { confirmed } = await confirm(deleteFilterConfirmProps(filterName));
    if (!confirmed) {
      return;
    }

    const { errors } = await deleteFilter({
      variables: { input: { audienceSearchFilterId: filterId } },
    });
    if (errors) {
      enqueueSnackbar("Failed to delete filter", { variant: "error" });
      return;
    }

    clearFilters();

    enqueueSnackbar("Filter deleted", { variant: "success" });
  }

  async function handleCreateFilter(formData: { name: string }) {
    const { errors, data } = await createFilter({
      variables: {
        input: {
          subaccountId: subaccount.id,
          audienceId,
          searchBody: convertTargetingFormToGraphQL({
            targetGroups: currentFilter.targetGroups,
          })!,
          name: formData.name,
          type: AudienceSearchFilterType.SavedSearch,
          recalculate: true,
        },
      },
    });

    if (errors) {
      enqueueSnackbar("Failed to create filter", { variant: "error" });
      return;
    }

    if (data?.createAudienceSearchFilter) {
      const filterData = pick(data.createAudienceSearchFilter, [
        "id",
        "name",
        "searchBody",
        "type",
      ]);
      setNameModalOpen(false);
      setFilter({
        ...filterData,
        targetGroups: TargetingFormSchema.parse(
          data.createAudienceSearchFilter.searchBody
        ).targetGroups,
        contactCount:
          data.createAudienceSearchFilter.searchJob?.result?.contactCount,
      });
    }
  }

  useEffect(() => {
    // poll the current filter record while the job is running
    if (
      data?.getAudienceSearchFilter.searchJob &&
      jobInProgress(data.getAudienceSearchFilter.searchJob)
    ) {
      setSearchJobIsRunning(true);
      startPolling(1000);
    } else {
      setFilter({
        ...currentFilter,
        contactCount:
          data?.getAudienceSearchFilter.searchJob?.result?.contactCount,
      });
      setSearchJobIsRunning(false);
      stopPolling();
    }
    // we can't include currentFilter as a dep or it will cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentFilter.id,
    data?.getAudienceSearchFilter,
    startPolling,
    stopPolling,
    setSearchJobIsRunning,
  ]);

  return (
    <>
      <FormProvider {...formMethods}>
        <Dialog open={nameModalOpen} onClose={() => setNameModalOpen(false)}>
          <DialogTitle>Name and Save Search</DialogTitle>
          <DialogContent>
            <RhfTextField name="name" label="Saved Search Name" fullWidth />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNameModalOpen(false)}>Cancel</Button>
            <Button
              onClick={formMethods.handleSubmit(handleCreateFilter)}
              type="submit"
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </FormProvider>
      <FlexBox
        justifyContent="space-between"
        sx={{
          backgroundColor: palette.lightGray,
          py: 3,
          px: 5,
        }}
      >
        <FlexBox direction="column">
          <SearchResultHeading filter={currentFilter} />
          <ReadableTargetGroupSummary
            targetGroups={currentFilter.targetGroups}
          />
        </FlexBox>
        <FlexBox alignItems="flex-end">
          <FlexBox gap={2}>
            {currentFilter.type === AudienceSearchFilterType.UserSearch ? (
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={SMALL_BUTTON_STYLE}
                  onClick={() => {
                    clearFilters();
                  }}
                >
                  <>
                    <Close sx={{ fontSize: "12px", mr: 0.5 }} />
                    Clear Filters
                  </>
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={SMALL_BUTTON_STYLE}
                  onClick={() => toggleIsEditingExistingFilter()}
                >
                  Edit Filters
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  sx={SMALL_BUTTON_STYLE}
                  onClick={() => setNameModalOpen(true)}
                >
                  Save Search
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={SMALL_BUTTON_STYLE}
                  onClick={() => {
                    clearFilters();
                  }}
                >
                  <>
                    <Close sx={{ fontSize: "12px", mr: 0.5 }} />
                    Clear Filters
                  </>
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={SMALL_BUTTON_STYLE}
                  loading={searchJobIsRunning}
                  onClick={async () => {
                    await enqueueAudienceSearchJob({
                      variables: {
                        input: { audienceSearchFilterId: currentFilter.id! },
                      },
                    });
                  }}
                >
                  Refresh Estimate
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={SMALL_BUTTON_STYLE}
                  onClick={() => toggleIsEditingExistingFilter()}
                >
                  Edit Filters
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={SMALL_BUTTON_STYLE}
                  onClick={async () =>
                    await handleDeleteFilter(
                      currentFilter.id!,
                      currentFilter.name!
                    )
                  }
                >
                  Delete Saved Search
                </Button>
              </>
            )}
          </FlexBox>
        </FlexBox>
      </FlexBox>
    </>
  );
}

export function SavedSearches({
  audienceId,
}: {
  audienceId: string;
}): ReactElement {
  const confirm = useConfirm();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [page, setPage] = useQueryParam(
    "saved_searches_page",
    withDefault(NumberParam, 0)
  );
  const [rowsPerPage, setRowsPerPage] = useQueryParam(
    "saved_searches_page_size",
    withDefault(NumberParam, 10)
  );
  const { subaccount } = useSubaccount()!;

  const setFilter = useAudience(store => store.setFilter);
  const setIsEditingExistingFilter = useAudience(
    store => store.setIsEditingExistingFilter
  );
  const clearFilters = useAudience(store => store.clearFilters);

  const { data, loading, error, startPolling, stopPolling } = useQuery<
    ListAudienceSearchFiltersQuery,
    ListAudienceSearchFiltersQueryVariables
  >(LIST_AUDIENCE_SEARCH_FILTERS, {
    variables: {
      input: {
        audienceId,
        subaccountId: subaccount.id,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      },
    },
  });

  const [deleteFilter] = useMutation<
    DeleteAudienceSearchFilterMutation,
    DeleteAudienceSearchFilterMutationVariables
  >(DELETE_AUDIENCE_SEARCH_FILTER, {
    refetchQueries: [LIST_AUDIENCE_SEARCH_FILTERS],
  });

  const [enqueueAudienceSearchJob] = useMutation<
    EnqueueAudienceContactSearchMutation,
    EnqueueAudienceContactSearchMutationVariables
  >(ENQUEUE_AUDIENCE_CONTACT_SEARCH, {
    onErrorSnackbarMessage: "Failed to apply the filters",
    awaitRefetchQueries: true,
    refetchQueries: [LIST_AUDIENCE_SEARCH_FILTERS],
  });

  const handleDeleteFilter = async (filterId: string, filterName: string) => {
    const { confirmed } = await confirm(deleteFilterConfirmProps(filterName));
    if (!confirmed) {
      return;
    }

    const { errors } = await deleteFilter({
      variables: { input: { audienceSearchFilterId: filterId } },
    });
    if (errors) {
      enqueueSnackbar("Failed to delete filter", { variant: "error" });
      return;
    }
    clearFilters();
    enqueueSnackbar("Filter deleted", { variant: "success" });
  };

  return (
    <FlexBox direction="column" sx={{ mt: 1, mb: 3 }}>
      <FlexBox
        justifyContent="space-between"
        sx={{
          backgroundColor: palette.lightGray,
          padding: theme.spacing(1, 2),
          ":hover": { cursor: "pointer" },
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Typography variant="body2Bold">Saved Searches</Typography>

        <ExpandButton
          onClick={() => setIsOpen(!isOpen)}
          open={isOpen}
          svgSx={{ fontSize: "14px" }}
        />
      </FlexBox>
      <Collapse in={isOpen}>
        <FlexBox
          direction="column"
          data-testid="saved-searches-container"
          sx={{
            backgroundColor: palette.lightGray,
            px: 5,
            pt: 1,
            pb: 3,
            borderTop: `1px solid ${palette.lightestGray}`,
          }}
        >
          <AsyncWrapper data={data} loading={loading} errors={error}>
            {({ listAudienceSearchFilters }) => {
              if (listAudienceSearchFilters.totalCount === 0) {
                return (
                  <FlexBox
                    alignItems="center"
                    justifyContent="center"
                    sx={{ pt: 2, px: 5 }}
                  >
                    <Typography variant="body2">
                      You don’t have any saved searches yet. Use the “Filter
                      Contacts” button above to create a search and save it.
                    </Typography>
                  </FlexBox>
                );
              }

              if (
                listAudienceSearchFilters.audienceSearchFilters.filter(
                  filter => filter.searchJob && jobInProgress(filter.searchJob)
                ).length === 0
              ) {
                stopPolling();
              } else {
                startPolling(1000);
              }

              return (
                <Table<
                  ListAudienceSearchFiltersQuery["listAudienceSearchFilters"]["audienceSearchFilters"][number]
                >
                  containerSx={{
                    "& .MuiTableHead-root": {
                      borderBottom: `2px solid ${palette.midGray}`,
                      "& .MuiTableCell-root": {
                        fontWeight: 600,
                      },
                    },
                  }}
                  rows={listAudienceSearchFilters.audienceSearchFilters}
                  totalCount={listAudienceSearchFilters.totalCount}
                  page={page}
                  onChangePage={setPage}
                  rowsPerPage={rowsPerPage}
                  onChangeRowsPerPage={setRowsPerPage}
                  columns={[
                    {
                      title: "Saved Search",
                      key: "name",
                      sx: { verticalAlign: "top" },
                      renderValue: row => {
                        return (
                          <FlexBox direction="column">
                            <Typography variant="body2Bold">
                              {row.name}
                            </Typography>
                            <ReadableTargetGroupSummary
                              targetGroups={
                                row.searchBody
                                  .targetGroups as TargetingForm["targetGroups"]
                              }
                            />
                          </FlexBox>
                        );
                      },
                    },
                    {
                      title: "Created",
                      key: "createdAt",
                      align: "left",
                      sx: { verticalAlign: "top" },
                      renderValue: row => (
                        <Box>
                          <Typography variant="body3">
                            {DateTime.fromMillis(row.createdAt).toLocaleString(
                              DateTime.DATETIME_SHORT
                            )}{" "}
                            by {row.createdBy?.fullName}
                          </Typography>
                        </Box>
                      ),
                    },
                    {
                      title: "Contact Estimate",
                      key: "contactEstimate",
                      align: "left",
                      sx: { verticalAlign: "top" },
                      renderValue: row => {
                        if (row.searchJob && jobInProgress(row.searchJob)) {
                          return (
                            <FlexBox>
                              <Loading size={24} style={{ marginTop: 0 }} />
                            </FlexBox>
                          );
                        }

                        return (
                          <FlexBox direction="column">
                            <Typography variant="body3" component="div">
                              {plural(
                                row.searchJob?.result?.contactCount || 0,
                                "contact",
                                "contacts"
                              )}
                            </Typography>
                            <Typography variant="body3" component="div">
                              {plural(
                                row.searchJob?.result?.optedOutContactCount ||
                                  0,
                                "opted out contact",
                                "opted out contacts"
                              )}
                            </Typography>
                            {row.searchJob?.result && (
                              <Typography variant="body3" component="div">
                                Calculated{" "}
                                {row.searchJob?.result?.calculatedAt &&
                                  DateTime.fromMillis(
                                    row.searchJob?.result?.calculatedAt
                                  ).toLocaleString(DateTime.DATETIME_SHORT)}
                              </Typography>
                            )}
                            <Typography variant="body3" component="div">
                              <LinkButton
                                onClick={async () => {
                                  await enqueueAudienceSearchJob({
                                    variables: {
                                      input: { audienceSearchFilterId: row.id },
                                    },
                                  });
                                }}
                              >
                                Refresh
                              </LinkButton>
                            </Typography>
                          </FlexBox>
                        );
                      },
                    },
                  ]}
                  actionSx={{ verticalAlign: "top" }}
                  rowActions={() => {
                    return [
                      {
                        id: "delete",
                        type: "button",
                        icon: (
                          <Delete
                            sx={{
                              color: palette.darkGray,
                              height: "20px !important",
                              width: "20px !important",
                            }}
                          />
                        ),
                        tooltipLabel: "Delete",
                        sx: { px: 0.5 },
                        onClick: async row => {
                          await handleDeleteFilter(row.id, row.name);
                        },
                      },
                      {
                        id: "edit",
                        type: "button",
                        icon: (
                          <Edit
                            sx={{
                              color: palette.darkGray,
                              height: "20px !important",
                              width: "20px !important",
                            }}
                          />
                        ),
                        tooltipLabel: "Edit",
                        sx: { pl: 0.5, fontSize: "14px" },
                        onClick: row => {
                          setFilter({
                            id: row.id,
                            targetGroups: row.searchBody
                              .targetGroups as TargetingForm["targetGroups"],
                            contactCount: row.searchJob?.result?.contactCount,
                            optedOutContactCount:
                              row.searchJob?.result?.optedOutContactCount,
                            name: row.name,
                            type: row.type,
                          });
                          setIsEditingExistingFilter(true);
                          setIsOpen(false);
                        },
                      },
                      {
                        id: "apply",
                        type: "button",
                        variant: "contained",
                        color: "accessory",
                        label: "View Contacts",
                        sx: {
                          backgroundColor: `${palette.midGray} !important`,
                          ...SMALL_BUTTON_STYLE,
                        },
                        onClick: row => {
                          setFilter({
                            targetGroups: TargetingFormSchema.parse(
                              row.searchBody
                            ).targetGroups,
                            id: row.id,
                            name: row.name,
                            contactCount: row.searchJob?.result?.contactCount,
                            optedOutContactCount:
                              row.searchJob?.result?.optedOutContactCount,
                            type: row.type,
                          });
                          setIsOpen(false);
                        },
                      },
                    ];
                  }}
                />
              );
            }}
          </AsyncWrapper>
        </FlexBox>
      </Collapse>
    </FlexBox>
  );
}
